import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Image,
  Linking,
  Platform,
} from 'react-native';
import Constants from 'expo-constants';
import { MaterialIcons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { RootState } from '../../store';
import { RootStackParamList } from '../../navigation/stackTypes';
import { useMenu } from '../../navigation/MenuContext';
import { UserSessionAttributes } from '../../models/user-session-model';
import { UserAttributes } from '../../models/login-register-model';
import { ActiveChargeAttributes } from '../../models/active-charges-model';
import RoleService from '../../services/role-service';
import MenuIcon, { MenuIconName } from './MenuIcon';
import { BottomSheetModalRef } from '../BottomSheet';
import SignOutOptions from '../../pages/SignOutOptions';
import SignInOptions from '../../pages/SignInOptions';
import ActiveChargeMenuCard from './ActiveChargeMenuCard';
import { accessibleTextProps } from '../../utils/accessibility-utils';

// Web-specific components
import { 
  WebOverlayContainer, 
  WEB_MENU_WIDTH, 
  ANIMATION_DURATION,
  Z_INDEX,
} from '../web';

const MENU_WIDTH = WEB_MENU_WIDTH;

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface MenuItem {
  id: string;
  label: string;
  icon: MenuIconName;
  route?: keyof RootStackParamList;
  params?: any;
  onPress?: () => void;
  color?: string;
  isExternal?: boolean;
}

interface MenuSection {
  id: string;
  title?: string;
  titleColor?: string;
  items: MenuItem[];
  condition: boolean;
}

/**
 * Web implementation of SideMenu using fixed positioning (same as native)
 */
const SideMenu: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const insets = useSafeAreaInsets();
  const { isMenuOpen, closeMenu } = useMenu();

  // Redux state
  const userSession: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );
  const user: UserAttributes | undefined = useSelector(
    (state: RootState) => state.user.userObj
  );
  const activeCharges: ActiveChargeAttributes[] | null = useSelector(
    (state: RootState) => state.user.activeCharges
  );

  // Authentication status
  const isAuthenticated = !!userSession && !RoleService.isGuest(userSession);
  const isGuest = RoleService.isGuest(userSession);
  const isHost = RoleService.hasRole(userSession, 'host');
  const isPartner = RoleService.hasRole(userSession, 'partner');
  const isAdmin = RoleService.isAdmin(userSession);
  
  // Get user's chargers for insights route params
  const myChargers = useSelector((state: RootState) => state.chargers.myChargers) || [];

  // Bottom sheet refs
  const bottomSheetModalRef = useRef<BottomSheetModalRef>(null);
  const bottomSheetSigninRef = useRef<BottomSheetModalRef>(null);

  // Accordion state
  const [isAccountExpanded, setIsAccountExpanded] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);

  // Check for onboarding tasks
  const totalTasks = (user?.onboarding_tasks?.length ?? 0) + (user?.partner_tasks?.length ?? 0);
  const hasOnboardingTasks = totalTasks > 0;
  const showOnboardingInMainMenu = (isHost || isPartner) && hasOnboardingTasks;

  // Reset accordion when menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setIsAccountExpanded(false);
    }
  }, [isMenuOpen]);

  // Handle escape key to close menu (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        closeMenu();
      }
    };

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isMenuOpen, closeMenu]);

  // Navigate and close menu
  const navigateTo = useCallback((route: keyof RootStackParamList, params?: any) => {
    closeMenu();
    setTimeout(() => {
      navigation.navigate(route as any, params);
    }, 100);
  }, [navigation, closeMenu]);

  // Handle accordion toggle for My Account
  const toggleAccountAccordion = useCallback(() => {
    const willExpand = !isAccountExpanded;
    setIsAccountExpanded(willExpand);
    
    // Scroll down when expanding to show the accordion content
    if (willExpand && scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 150);
    }
  }, [isAccountExpanded]);

  // Open sign out sheet
  const openSignOutSheet = useCallback((event?: any) => {
    // Prevent event propagation to avoid triggering the RBSheet mask click
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    closeMenu();
    // Use a longer delay to ensure menu is fully closed and all events have settled
    // before opening the bottom sheet
    setTimeout(() => {
      // Double-check we're not in a click event state by using requestAnimationFrame
      requestAnimationFrame(() => {
        bottomSheetModalRef.current?.present();
      });
    }, 350);
  }, [closeMenu]);

  // Open sign in sheet
  const openSignInSheet = useCallback(() => {
    closeMenu();
    setTimeout(() => {
      bottomSheetSigninRef.current?.present();
    }, 200);
  }, [closeMenu]);

  // Open external link
  const openExternalLink = useCallback((url: string) => {
    closeMenu();
    Linking.openURL(url);
  }, [closeMenu]);

  // Render menu item
  const renderMenuItem = useCallback((item: MenuItem, isSubItem: boolean = false) => (
    <Pressable
      key={item.id}
      onPress={() => {
        if (item.onPress) {
          item.onPress();
        } else if (item.isExternal && item.route) {
          openExternalLink(item.route as string);
        } else if (item.route) {
          navigateTo(item.route, item.params);
        }
      }}
      style={({ pressed }) => [
        styles.menuItem,
        isSubItem && styles.subMenuItem,
        pressed && { opacity: 0.6 },
      ]}
    >
      <View style={styles.menuItemContent}>
        <MenuIcon name={item.icon} size={20} color={item.color || '#FFFFFF'} />
        <Text
          {...accessibleTextProps.menuItem}
          style={[
            styles.menuItemText,
            { color: item.color || '#FFFFFF' },
            isSubItem && styles.subMenuItemText,
          ]}
        >
          {item.label}
        </Text>
      </View>
      <MaterialIcons
        name="keyboard-arrow-right"
        size={20}
        color={item.color || '#FFFFFF'}
      />
    </Pressable>
  ), [navigateTo, openExternalLink]);

  // Menu sections configuration - mirrors native app structure
  const menuSections: MenuSection[] = [
    // Main section (for authenticated users) - no title, just items
    {
      id: 'main',
      condition: isAuthenticated,
      items: [
        {
          id: 'findCharger',
          label: 'Find a Charger',
          icon: 'findACharger',
          route: 'ChargerSelection',
          params: { tabSelect: 'Map', key: 'Map', title: 'Map' },
        },
        {
          id: 'chargingHistory',
          label: 'Charging History',
          icon: 'chargingHistory',
          route: 'TransactionHistory',
        },
      ],
    },
    // Become a Host - shown only for authenticated users who are not
    // already a host/partner/admin. On web this routes to BecomeHost,
    // which shows a "download the app" gate.
    {
      id: 'becomeHost',
      condition: isAuthenticated && !isHost && !isPartner && !isAdmin,
      items: [
        {
          id: 'becomeHost',
          label: 'Become a Host',
          icon: 'chargerDashboard',
          route: 'BecomeHost',
          color: '#FFD700',
        },
      ],
    },
    // Host Section (for hosts only)
    {
      id: 'host',
      title: 'MEANDEREV HOST',
      titleColor: '#40BFB4',
      condition: isHost && isAuthenticated,
      items: [
        {
          id: 'chargerDashboard',
          label: 'My Chargers',
          icon: 'chargerDashboard',
          route: 'MyChargers',
        },
        {
          id: 'chargerInsights',
          label: 'Charger Insights',
          icon: 'chargerInsights',
          route: 'HostInsights',
          params: { preselectedChargerIds: myChargers.map((c: any) => c.uuid).filter(Boolean) },
        },
      ],
    },
    // Partner Section (for partners only)
    {
      id: 'partner',
      title: 'MEANDEREV PARTNER',
      titleColor: '#3B82F6',
      condition: isPartner && isAuthenticated,
      items: [
        {
          id: 'partnerPortal',
          label: 'Partner Portal',
          icon: 'partnerPortal',
          route: 'PartnerPortal',
        },
        {
          id: 'earningsDashboard',
          label: 'Earnings Dashboard',
          icon: 'earningsDashboard',
          route: 'PartnerEarningsDashboard',
        },
      ],
    },
    // Admin Section (for admins only)
    {
      id: 'admin',
      title: 'ADMIN',
      titleColor: '#00D9FF',
      condition: isAdmin && isAuthenticated,
      items: [
        {
          id: 'adminPortal',
          label: 'Admin Control Panel',
          icon: 'chargerDashboard',
          route: 'AdminScreen',
          color: '#00D9FF',
        },
      ],
    },
    // Onboarding (for hosts/partners with tasks)
    {
      id: 'onboarding',
      condition: showOnboardingInMainMenu,
      items: [
        {
          id: 'onboardingStatus',
          label: `Onboarding Status (${totalTasks})`,
          icon: 'myAccount',
          route: 'OnboardingStatus',
          color: '#FFD700',
        },
      ],
    },
    // Guest Section
    {
      id: 'guest',
      title: 'GET STARTED',
      titleColor: '#40BFB4',
      condition: isGuest,
      items: [
        {
          id: 'findChargerGuest',
          label: 'Find a Charger',
          icon: 'findACharger',
          route: 'ChargerSelection',
          params: { tabSelect: 'Map' },
        },
      ],
    },
  ];

  // Account sub-menu items
  const accountSubItems: MenuItem[] = isAuthenticated
    ? [
        {
          id: 'updateAccountInfo',
          label: 'Update Account Info',
          icon: 'myAccount',
          route: 'AccountInfo',
        },
        {
          id: 'changePassword',
          label: 'Change Password',
          icon: 'myAccount',
          route: 'AccountInfo',
          params: { changePassword: true },
        },
        {
          id: 'redeemedCodes',
          label: 'Redeemed Codes',
          icon: 'chargerDashboard' as MenuIconName,
          route: 'RedeemedCodes' as keyof RootStackParamList,
        },
        ...(showOnboardingInMainMenu
          ? []
          : isHost || isPartner
          ? [
              {
                id: 'onboardingStatusSub',
                label: 'Onboarding Status',
                icon: 'myAccount' as MenuIconName,
                route: 'OnboardingStatus' as keyof RootStackParamList,
              },
            ]
          : []),
        ...(isHost
          ? [
              {
                id: 'payoutRules',
                label: 'Payout Rules',
                icon: 'chargerDashboard' as MenuIconName,
                route: 'PayoutRules' as keyof RootStackParamList,
              },
            ]
          : []),
        {
          id: 'deleteAccount',
          label: 'Delete Account',
          icon: 'signOut',
          color: '#FF6B6B',
          isExternal: true,
          route: 'https://meanderev.com/delete-data/' as any,
        },
      ]
    : [];

  // Debug: Log when menu state changes
  console.log('[SideMenu.web] isMenuOpen:', isMenuOpen);

  return (
    <>
      {/* Web-specific overlay container - handles positioning within app boundaries */}
      <WebOverlayContainer
        visible={isMenuOpen}
        onBackdropPress={closeMenu}
        showBackdrop={true}
        zIndex={Z_INDEX.OVERLAY_BACKDROP}
      >
        {/* Menu Panel - slides in from left edge */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: MENU_WIDTH,
            backgroundColor: '#1A1D21',
            zIndex: 1,
            transform: `translateX(${isMenuOpen ? 0 : -MENU_WIDTH}px)`,
            transition: `transform ${ANIMATION_DURATION.MENU_SLIDE}ms ease-in-out`,
            boxShadow: isMenuOpen ? '2px 0 10px rgba(0, 0, 0, 0.25)' : 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
        {/* Close button */}
        <Pressable
          style={[styles.closeButton, { top: (insets.top || 16) + 12 }]}
          onPress={closeMenu}
        >
          <MaterialIcons name="close" size={24} color="#FFFFFF" />
        </Pressable>

        {/* Logo and User Info */}
        <View style={[styles.logoContainer, { paddingTop: (insets.top || 16) + 48 }]}>
          <Image
            source={require('../../../assets/meanderlogowhite_120x120.png')}
            style={styles.logo}
          />
          <Text style={styles.logoText}>MEANDER<Text style={styles.logoAccent}>EV</Text></Text>
          {/* User Email */}
          {isAuthenticated && userSession?.email && (
            <Text style={styles.userEmail} numberOfLines={1}>
              {userSession.email}
            </Text>
          )}
          {isGuest && (
            <Text style={styles.guestLabel}>Guest</Text>
          )}
        </View>

        {/* Active Charge Card (if applicable) */}
        {activeCharges && activeCharges.length > 0 && (
          <ActiveChargeMenuCard
            activeCharges={activeCharges}
            onPress={() => {
              try {
                closeMenu();
                const activeCharge = activeCharges[0];
                if (!activeCharge?.order?.id) {
                  console.warn('[SideMenu] Active charge missing order data');
                  return;
                }
                const retOrder = activeCharge.order;
                if (retOrder.status === 'PENDING_RECONCILIATION') {
                  navigation.navigate('ChargeSummary', {
                    charger: activeCharge.charger,
                    startTime: new Date(retOrder.createdAt ?? Date.now()),
                    endTime: new Date(),
                    orderId: retOrder.id,
                    fromScreen: 'SideMenu',
                    pendingReconciliation: true,
                  });
                } else {
                  navigation.navigate('ChargingScreen', {
                    order: retOrder,
                    scheduleObj: activeCharge.schedule,
                  });
                }
              } catch (e) {
                console.error('[SideMenu] Error navigating to active charge:', e);
              }
            }}
          />
        )}

        {/* Scrollable Menu Content */}
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Render menu sections */}
          {menuSections.map((section) => {
            if (!section.condition) return null;

            return (
              <View key={section.id} style={styles.section}>
                {section.title && (
                  <Text
                    style={[
                      styles.sectionTitle,
                      { color: section.titleColor || '#8D8E90' },
                    ]}
                  >
                    {section.title}
                  </Text>
                )}
                {section.items.map((item) => renderMenuItem(item))}
              </View>
            );
          })}

          {/* Account and Information Section */}
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: '#40BFB4' }]}>
              ACCOUNT AND INFORMATION
            </Text>

            {/* My Account with accordion (for authenticated users) */}
            {isAuthenticated && (
              <>
                <Pressable
                  onPress={toggleAccountAccordion}
                  style={({ pressed }) => [
                    styles.menuItem,
                    pressed && { opacity: 0.6 },
                  ]}
                >
                  <View style={styles.menuItemContent}>
                    <MenuIcon name="myAccount" size={20} color="#FFFFFF" />
                    <Text {...accessibleTextProps.menuItem} style={styles.menuItemText}>
                      My Account
                    </Text>
                  </View>
                  <MaterialIcons
                    name={isAccountExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"}
                    size={20}
                    color="#FFFFFF"
                  />
                </Pressable>

                {/* Accordion content */}
                {isAccountExpanded && (
                  <View style={styles.accordionContent}>
                    {accountSubItems.map((item) => renderMenuItem(item, true))}
                  </View>
                )}
              </>
            )}

            {/* Help Center */}
            <Pressable
              onPress={() => openExternalLink('https://meanderev.com/faq')}
              style={({ pressed }) => [
                styles.menuItem,
                pressed && { opacity: 0.6 },
              ]}
            >
              <View style={styles.menuItemContent}>
                <MenuIcon name="helpCenter" size={20} color="#FFFFFF" />
                <Text {...accessibleTextProps.menuItem} style={styles.menuItemText}>
                  Help Center
                </Text>
              </View>
              <MaterialIcons name="keyboard-arrow-right" size={20} color="#FFFFFF" />
            </Pressable>

            {/* About */}
            <Pressable
              onPress={() => openExternalLink('https://www.meanderev.com')}
              style={({ pressed }) => [
                styles.menuItem,
                pressed && { opacity: 0.6 },
              ]}
            >
              <View style={styles.menuItemContent}>
                <MenuIcon name="about" size={20} color="#FFFFFF" />
                <Text {...accessibleTextProps.menuItem} style={styles.menuItemText}>
                  About
                </Text>
              </View>
              <MaterialIcons name="keyboard-arrow-right" size={20} color="#FFFFFF" />
            </Pressable>

            {/* Sign Out (for authenticated users) */}
            {isAuthenticated && (
              <Pressable
                onPress={(e) => openSignOutSheet(e)}
                style={({ pressed }) => [
                  styles.menuItem,
                  pressed && { opacity: 0.6 },
                ]}
              >
                <View style={styles.menuItemContent}>
                  <MenuIcon name="signOut" size={20} color="#FF6B6B" />
                  <Text
                    {...accessibleTextProps.menuItem}
                    style={[styles.menuItemText, { color: '#FF6B6B' }]}
                  >
                    Log Out
                  </Text>
                </View>
                <MaterialIcons name="keyboard-arrow-right" size={20} color="#FF6B6B" />
              </Pressable>
            )}

            {/* Sign In / Sign Up (for guests) */}
            {isGuest && (
              <Pressable
                onPress={openSignInSheet}
                style={({ pressed }) => [
                  styles.menuItem,
                  pressed && { opacity: 0.6 },
                ]}
              >
                <View style={styles.menuItemContent}>
                  <MenuIcon name="signInOrSignUp" size={20} color="#39BEB7" />
                  <Text
                    {...accessibleTextProps.menuItem}
                    style={[styles.menuItemText, { color: '#39BEB7' }]}
                  >
                    Log In or Sign Up
                  </Text>
                </View>
                <MaterialIcons name="keyboard-arrow-right" size={20} color="#39BEB7" />
              </Pressable>
            )}
          </View>

          {/* App version */}
          <Text style={styles.versionText}>
            v{Constants.expoConfig?.version ?? '—'}
            {Constants.expoConfig?.ios?.buildNumber
              ? ` (${Constants.expoConfig.ios.buildNumber})`
              : Constants.expoConfig?.android?.versionCode
              ? ` (${Constants.expoConfig.android.versionCode})`
              : ''}
          </Text>
        </ScrollView>
        </div>
      </WebOverlayContainer>

      {/* Bottom sheets */}
      <SignOutOptions
        navigation={navigation}
        bottomSheetModalRef={bottomSheetModalRef}
        destination="WelcomeScreen"
      />
      <SignInOptions
        navigation={navigation}
        bottomSheetModalRef={bottomSheetSigninRef}
        destination="ChargerSelection"
      />
    </>
  );
};

const styles = StyleSheet.create({
  closeButton: {
    position: 'absolute',
    left: 16,
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  logoContainer: {
    alignItems: 'center',
    paddingBottom: 16,
  },
  logo: {
    width: 70,
    height: 70,
  },
  logoText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    letterSpacing: 2,
    marginTop: 6,
  },
  logoAccent: {
    color: '#39BEB7',
  },
  userEmail: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 8,
    paddingHorizontal: 16,
    textAlign: 'center',
  },
  guestLabel: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 8,
    fontStyle: 'italic',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 40,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    letterSpacing: 1,
    marginBottom: 8,
    marginTop: 16,
    paddingLeft: 4,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 4,
  },
  subMenuItem: {
    paddingLeft: 32,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 8,
    marginLeft: 8,
  },
  menuItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  menuItemText: {
    fontSize: 16,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '500',
    color: '#FFFFFF',
  },
  subMenuItemText: {
    fontSize: 14,
  },
  accordionContent: {
    // Container for accordion content
  },
  versionText: {
    color: '#4A4D52',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    textAlign: 'center',
    paddingTop: 8,
    paddingBottom: 4,
  },
});

export default SideMenu;
